import { bookingRoomLabel } from '../utils/bookingRoomLabel';
import { bookingGuestNameLabel } from '../utils/bookingGuestLabel';
import { bookingHref } from '../utils/bookingHref';
import { CockpitPanelProvider, useCockpitPanel } from './cockpit/SidePanel';
import { useLayoutEffect, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookings } from '../hooks/useBookings';
import { useAuth } from '../contexts/AuthContext';
import { useCellFeatures } from '../contexts/CellFeaturesContext';
import { isRouteEnabled, isWidgetEnabled, peekTenantRuntime, propertyKeyOf } from '../config/tenant';
import { bookingSearchWindow, matchesBooking } from '../utils/bookingSearch';
import { safeFormatDate } from '../utils/dateUtils';
import type { Booking } from '../types/booking';
import { HouseChip } from './ui/HouseChip';
import { Kbd } from './ui/Kbd';
import { PaulaSession } from './cockpit/PaulaSession';

export interface CommandBarProps { mode: 'search' }

export function CommandBar(props: CommandBarProps) {
  const panel = useCockpitPanel();
  return panel ? <CommandBarBody {...props} /> : <CockpitPanelProvider><CommandBarBody {...props} /></CockpitPanelProvider>;
}
function CommandBarBody({ mode }: CommandBarProps) {
  const { user } = useAuth();
  const features = useCellFeatures();
  const searchEnabled = isRouteEnabled('/bookings', features);
  const paulaEnabled = isWidgetEnabled('agentChat', features);
  return user && (searchEnabled || paulaEnabled) ? <CommandModes key={`${user.uid}-${peekTenantRuntime()?.tenantId}-${searchEnabled}-${paulaEnabled}`} searchEnabled={searchEnabled} paulaEnabled={paulaEnabled} mode={mode} /> : null;
}

function CommandModes({ searchEnabled, paulaEnabled, mode }: CommandBarProps & { searchEnabled: boolean; paulaEnabled: boolean }) {
  const panel = useCockpitPanel()!;
  const paulaOpen = panel.selection?.id === 'paula';
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const restoringFocus = useRef(false);
  const previouslyOpen = useRef(false);
  useLayoutEffect(() => {
    if (previouslyOpen.current && !paulaOpen) {
      restoringFocus.current = true;
      queueMicrotask(() => { restoringFocus.current = false; });
    }
    previouslyOpen.current = paulaOpen;
  }, [paulaOpen]);
  const trigger = useRef<HTMLElement | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const openPaula = (value: string, element: HTMLElement) => { if (!paulaOpen) { trigger.current = element; setDraft(value); setQuery(''); } panel.open('paula', 'Frag Paula', trigger.current ?? element); };
  useEffect(() => {
    if (searchEnabled) return;
    const keyboard = (event: KeyboardEvent) => { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); if (button.current) openPaula('', button.current); } };
    document.addEventListener('keydown', keyboard);
    return () => document.removeEventListener('keydown', keyboard);
  }, [searchEnabled]);
  return <div data-mode={paulaOpen ? 'paula' : mode} className="ml-auto flex min-w-0 flex-1 items-center gap-2 md:max-w-lg">
    {searchEnabled && <BookingSearch mode={mode} query={query} setQuery={setQuery} restoringFocus={restoringFocus} onPaula={paulaEnabled ? openPaula : undefined} />}
    {paulaEnabled && <button ref={button} data-paula-trigger type="button" aria-expanded={paulaOpen} onClick={event => openPaula(query, event.currentTarget)} className="shrink-0 rounded-ctl border border-line px-2 py-1 text-sm">Frag Paula{!searchEnabled && <> <Kbd>⌘/Strg+K</Kbd></>}</button>}
    {paulaEnabled && <PaulaSession open={paulaOpen} draft={draft} trigger={trigger.current} onClose={panel.close} />}
  </div>;
}

function BookingSearch({ mode, onPaula, query, setQuery, restoringFocus }: CommandBarProps & { query: string; setQuery: (value: string) => void; restoringFocus: { current: boolean }; onPaula?: (draft: string, trigger: HTMLElement) => void }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [shortcut, setShortcut] = useState('Strg+K');
  const searchWindow = useMemo(() => bookingSearchWindow(), [open]);
  const { bookings, loading, error, refresh } = useBookings({ ...searchWindow, includeArchived: true, initialLimit: 500, allPages: true, enabled: open && !!query.trim() && !query.startsWith('?'), cacheMaxAge: 5 * 60 * 1000 });
  const matches = useMemo(() => bookings.filter(booking => matchesBooking(booking, query)), [bookings, query]);
  const close = () => { setOpen(false); setQuery(''); setActive(0); };
  useEffect(() => {
    setShortcut(/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Strg+K');
    const keyboard = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        if (document.activeElement instanceof Element && document.activeElement.closest('.cockpit-side-panel')) return;
        event.preventDefault(); setOpen(true); input.current?.focus();
      }
      if (event.key === 'Escape') close();
    };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-paula-trigger]')) setOpen(false);
      else if (!root.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('keydown', keyboard);
    document.addEventListener('pointerdown', outside);
    return () => { document.removeEventListener('keydown', keyboard); document.removeEventListener('pointerdown', outside); };
  }, []);
  const select = (booking: Booking) => {
    close(); input.current?.blur();
    navigate(bookingHref(booking.id || booking.bookingReference));
  };
  useEffect(() => {
    document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, listId]);
  return <div ref={root} data-mode={mode} className="relative ml-auto min-w-0 flex-1 md:max-w-sm" onBlur={event => {
    if (event.relatedTarget instanceof HTMLElement && event.relatedTarget.hasAttribute('data-paula-trigger')) setOpen(false);
    else if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
  }}>
    <div className="flex items-center gap-1 rounded-ctl border border-line bg-bg px-2">
      <input ref={input} type="text" role="combobox" aria-label="Suche Gast, Telefon, Buchung, Einheit"
        placeholder={onPaula ? '? Frag Paula oder suche …' : 'Suche Gast, Telefon, Buchung, Einheit'} autoComplete="off"
        aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
        className="h-8 min-w-0 flex-1 bg-bg text-[13px] text-ink placeholder:text-muted"
        value={query} onFocus={() => { if (restoringFocus.current) { restoringFocus.current = false; return; } setOpen(true); }} onChange={event => { const value = event.target.value; if (onPaula && value.startsWith('?')) { close(); onPaula(value.slice(1).trimStart(), event.currentTarget); return; } setQuery(value); setActive(0); setOpen(true); }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); setOpen(true);
            setActive(index => matches.length ? (index + (event.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length : 0);
          }
          if (event.key === 'Enter' && open && matches[active] && !loading && !error) { event.preventDefault(); select(matches[active]); }
        }} />
      <span><Kbd>{shortcut}</Kbd></span>
    </div>
    {open && <div className="fixed inset-x-3 top-12 cockpit-popover-layer mt-1 md:absolute md:inset-x-auto md:right-0 md:top-full md:w-[440px] overflow-hidden rounded-panel border border-line bg-panel p-1 text-sm text-ink">
      {loading ? <p role="status" className="bg-line-2 p-3">Lädt …</p>
        : error ? <div role="alert" className="p-3 text-crit">Buchungen nicht verfügbar: {error} <button className="rounded-ctl border border-line px-2 py-1" onClick={refresh}>Erneut versuchen</button></div>
        : <p role="status" className="px-2 py-1 text-xs text-muted">{query.trim() ? (matches.length ? `${matches.length} Treffer` : 'Keine Treffer') : 'Gast, Telefon, Buchung oder Einheit eingeben'}</p>}
      <ul id={listId} role="listbox" aria-label="Buchungstreffer" className="max-h-80 overflow-auto">
        {!loading && !error && matches.map((booking, index) => <li key={booking.id || booking.bookingReference} id={`${listId}-${index}`}
          role="option" aria-selected={active === index} className={active === index ? 'bg-signal-soft' : ''}>
          <button tabIndex={-1} onMouseDown={event => event.preventDefault()} onClick={() => select(booking)}
            className="flex w-full items-center gap-2 rounded-cell px-2 py-2 text-left">
            <HouseChip code={propertyKeyOf(booking) ?? booking.propertyShortName} />
            <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{bookingGuestNameLabel(booking)}</span>{booking.status === 'archived' && <span className="block text-xs text-muted">Archiviert</span>}
              <span className="block truncate text-xs text-muted">{bookingRoomLabel(booking)} · {safeFormatDate(booking.checkIn, 'dd.MM.yy')}–{safeFormatDate(booking.checkOut, 'dd.MM.yy')}</span></span>
          </button>
        </li>)}
      </ul>
    </div>}
  </div>;
}
